import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { ContentSection, LocalizedDate, TextField } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import startCase from 'lodash/startCase';

import { itemGroupsListQueryAtom } from '../../../_graphql/queryItemGroupsList';
import { $itemForm, ItemFormState } from '../../../_state/itemForm.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { ItemDetailRecord } from '../../../types/inventoryItem';

import { SelectCategory } from '~/shared/components/select-fields/SelectCategory';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectItemGroup } from '~/shared/components/select-fields/SelectItemGroup';

const $selectedItemGroupId = atom((get) => get($itemForm).itemGroupId);

const $selectedItemGroupData = itemGroupsListQueryAtom((get) => {
  const groupId = get($selectedItemGroupId);
  return {
    variables: { ids: groupId ? [groupId] : undefined, limit: 1 },
    skip: !groupId,
  };
});

const $groupDefaultPricingUnit = atom((get) => {
  const result = get($selectedItemGroupData);
  return result.data?.itemGroups?.[0]?.defaultPricingUnit ?? null;
});

type ItemFormFieldsProps = {
  disabled?: boolean;
  showTimestamps?: boolean;
  showAttributes?: boolean;
  item?: ItemDetailRecord | null;
  className?: string;
};

const itemFormSelector = (state: ItemFormState) => ({
  name: state.name,
  skuNumber: state.skuNumber,
  categoryId: state.categoryId,
  itemGroupId: state.itemGroupId,
  pricingUnitOverride: state.pricingUnitOverride,
  description: state.description,
  notes: state.notes,
});

export const ItemFormFields = ({
  disabled = false,
  showTimestamps = false,
  showAttributes = false,
  item,
  className,
}: ItemFormFieldsProps) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($itemForm, itemFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($itemForm);
  const groupDefaultUnit = useAtomValue($groupDefaultPricingUnit);

  const handleFieldChange = React.useCallback(
    <K extends keyof ItemFormState>(field: K) =>
      (value: ItemFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  return (
    <div className={cn('@ItemFormFields', className)}>
      <ContentSection
        title={t.inventoryView.itemDetailView.sections.itemInformation}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.inventoryView.itemEditDialog.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            placeholder={t.inventoryView.itemEditDialog.placeholders.name}
            isRequired
            isDisabled={disabled}
          />

          <TextField
            label={t.inventoryView.itemEditDialog.fields.skuNumber}
            value={form.skuNumber}
            onChange={(value) => handleFieldChange('skuNumber')(value)}
            placeholder={t.inventoryView.itemEditDialog.placeholders.skuNumber}
            isRequired
            isDisabled={disabled}
          />

          <SelectCategory
            label={t.inventoryView.itemEditDialog.fields.category}
            value={form.categoryId}
            onChange={(value) => handleFieldChange('categoryId')(value)}
            placeholder={t.inventoryView.itemEditDialog.placeholders.category}
            isRequired
            isDisabled={disabled}
          />

          <SelectItemGroup
            label={t.inventoryView.itemEditDialog.fields.itemGroup}
            value={form.itemGroupId}
            onChange={(value) => handleFieldChange('itemGroupId')(value)}
            placeholder={t.inventoryView.itemEditDialog.placeholders.itemGroup}
            isRequired
            isDisabled={disabled}
          />

          <SelectEnum<Unit>
            label={t.inventoryView.itemEditDialog.fields.pricingUnitOverride}
            enumValue={enumUnit}
            value={form.pricingUnitOverride as Unit | undefined}
            onChange={(value) =>
              handleFieldChange('pricingUnitOverride')(value ?? undefined)
            }
            placeholder={
              groupDefaultUnit
                ? `Inherit from group (${startCase(groupDefaultUnit)})`
                : t.inventoryView.itemEditDialog.placeholders
                    .pricingUnitOverride
            }
            showAllOption
            allOptionLabel={
              groupDefaultUnit
                ? `Inherit from group (${startCase(groupDefaultUnit)})`
                : t.inventoryView.itemEditDialog.placeholders
                    .pricingUnitOverride
            }
            isDisabled={disabled}
          />
        </div>
      </ContentSection>

      {showTimestamps && (
        <ContentSection
          title={t.inventoryView.itemDetailView.sections.timestamps}
          variant="panel"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.itemDetailView.fields.created}
              </span>
              <span className="text-sm font-medium">
                {item?.timestamps.createdAt ? (
                  <LocalizedDate date={item.timestamps.createdAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.itemDetailView.fields.updated}
              </span>
              <span className="text-sm font-medium">
                {item?.timestamps.updatedAt ? (
                  <LocalizedDate date={item.timestamps.updatedAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
          </div>
        </ContentSection>
      )}

      <ContentSection
        title={t.inventoryView.itemEditDialog.sections.additional}
        variant="panel"
        hideDivider={
          !showAttributes ||
          !item ||
          Object.keys(item.attributeValues).length === 0
        }
      >
        <div className="grid gap-4">
          <TextField
            label={t.inventoryView.itemEditDialog.fields.description}
            value={form.description}
            onChange={(value) => handleFieldChange('description')(value)}
            placeholder={
              t.inventoryView.itemEditDialog.placeholders.description
            }
            isDisabled={disabled}
          />
          <TextField
            label={t.inventoryView.itemEditDialog.fields.notes}
            value={form.notes}
            onChange={(value) => handleFieldChange('notes')(value)}
            placeholder={t.inventoryView.itemEditDialog.placeholders.notes}
            textArea
            isDisabled={disabled}
          />
        </div>
      </ContentSection>

      {showAttributes &&
        item &&
        Object.keys(item.attributeValues).length > 0 && (
          <ContentSection
            title={t.inventoryView.itemDetailView.sections.attributes}
            variant="panel"
            hideDivider
          >
            <dl className="space-y-3">
              {Object.entries(item.attributeValues).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <dt className="text-sm text-neutral/60">{key}</dt>
                  <dd className="text-sm font-medium">{String(value)}</dd>
                </div>
              ))}
            </dl>
          </ContentSection>
        )}
    </div>
  );
};
